import _ from 'lodash'
import commonJavascriptSuggestions from './CommonJavascriptSuggestions'
import stringExtensions from './StringExtensions'
import { getDataSource } from './DataSource'

/**
 * Completion: a type which is used to auto-complete lines in a flow
 * @typedef { Object } Completion
 * @memberof CodeCompletionsService
 */
type Completion = {
  text: string,
  type: string,
  display: string
}

const suggestionCache = {}

/**
 * Returns a list of global variables/functions etc for autocompletion
 * @function
 * @memberof CodeCompletionsService
 *
 * @return { Array<Completion> } List of possible global completions for this type of app.
 */
const defaultExamples = [
  // DIALOG
  {
    text: `Dialog.info('header', 'text', { 'timeout': 10 });`,
    type: 'dialog',
    display: 'Info dialog with 10 sec timeout'
  },
  {
    text: `Dialog.warn('header', 'text', { 'timeout': 10 });`,
    type: 'dialog',
    display: 'Warning dialog with 10 sec timeout'
  },
  {
    text: `Dialog.input('header', 'text', { 'name': { 'prompt': 'Enter name', 'type': 'TEXT' }})`,
    type: 'dialog',
    display: 'Input dialog with one simple text input'
  },
  {
    text: `Dialog.input('header', 'text', { 'name': { 'prompt': 'Choose color', 'type': 'SELECT', 'selectBetween': ['red','green','blue']}});`,
    type: 'dialog',
    display: 'Input dialog with one simple select input'
  },
  {
    text: `Dialog.inputHtml('Header', 'Some more text', { source: "<input type='text' id='myText'></input>", embed: true});`,
    type: 'dialog',
    display: 'Input dialog with html input'
  },
  // FLOW
  {
    text: `Flow.run('name', { 'a': 10, 'b': 20 });`,
    type: 'flow',
    display: 'Run a named flow with some input'
  },
  // WAIT
  {
    text: `Wait.forSeconds(n);`,
    type: 'wait',
    display: 'Wait for n seconds'
  },
  {
    text: `Wait.forMilliseconds(n);`,
    type: 'wait',
    display: 'Wait for n milliseconds'
  },
  {
    text: `Wait.forField(Fields['myfield'], 10);`,
    type: 'wait',
    display: 'Wait for a field to appear (w 10 sec timeout)'
  },
  {
    text: `Wait.forFieldToDisappear(Fields['myfield'], 10);`,
    type: 'wait',
    display: 'Wait for a field to disappear (w 10 sec timeout)'
  },
  // EXCEL
  {
    text: `Excel.load(file, {});`,
    type: 'excel',
    display: 'Load a complete spreadsheet'
  },
  {
    text: `Excel.load(file, { table: { range: 'A1:D3', sheet: 'Sheet1' } });`,
    type: 'excel',
    display: 'Load a specific range in a specific spreadsheet'
  },
  {
    text: `Excel.load(file, { table: { range: 'A1:D3', sheet: 'Sheet1', header: true } });`,
    type: 'excel',
    display: 'Load a specific range in a specific spreadsheet - first line is header'
  },
  {
    text: `Excel.updateCell('data.xlsx', 'Sheet1', 'A3', 1000);`,
    type: 'excel',
    display: 'Update the value stored in a single cell in a spreadsheet.'
  },
  {
    text: `Excel.updateCells('data.xlsx', 'Sheet1', 'A1', data);`,
    type: 'excel',
    display: 'Update multiple cells.'
  },
  // DEBUG
  { text: `Debug.show();`, type: 'debug', display: 'Debug.show()' },
  {
    text: `Debug.showDialog('Debugged!');`,
    type: 'debug',
    display: "Debug.showDialog('Debugged!')"
  },
  {
    text: `Debug.ger()`,
    type: 'debug',
    display: 'Debug.ger()',
    documentation:
      'The Debug.ger() method pauses the running flow (as any other dialog) and shows a debugger dialog which includes an inspector and a REPL (read-eval-print loop).'
  },
  // FS
  {
    text: `Fs.ls('c:\\somedir\\somefile*.txt')`,
    type: 'fs',
    display: 'List files on local machine'
  },
  {
    text: `Fs.write('c:\\\\somedir\\\\somefile.html', '<html><body><h1>Generated html!</h1></body></html>');`,
    type: 'fs',
    display: 'Write to file.',
    documentation:
      'Writes arbitrary text to an arbitrary text file. If the file exists, it will be overwritten. If the file doesn’t exist, it will be created with the given contents. The contents are written using UTF-8 encoding without a byte order mark (BOM).\n' +
      '\n' +
      'Throws appropriate exceptions if the write fails.'
  },
  // FTP
  {
    text: `Ftp.read('ftp://somewhere/over/the/rainbow.txt', {})`,
    type: 'ftp',
    display: 'Read a file from a remote ftp server anonymously'
  },
  {
    text: `Ftp.read('ftp://somewhere/over/the/rainbow.txt', { 'user': 'John', 'pass': 'ramb0' })`,
    type: 'ftp',
    display: 'Read a file from a remote ftp server with a username and password'
  },
  {
    text: `Ftp.write('ftp://somewhere/over/the/rainbow.txt', "red, green, blue", {})`,
    type: 'ftp',
    display: 'Write a file to a remote ftp server'
  },
  // XML (and XmlDoc)
  {
    text: `var doc = Xml.load('<hello>world</hello>');`,
    type: 'xml',
    display: 'Load and parse an xml string'
  },
  {
    text: `var doc = Xml.loadFrom('http://xmlserver/doc.xml');`,
    type: 'xml',
    display: 'Load and parse an xml file from a url'
  },
  {
    text: `doc.xpath('//hello')`,
    type: 'xml',
    display: 'Execute an xpath query agains an XmlDoc'
  },
  {
    text: `doc.json()`,
    type: 'xml',
    display: 'Convert an XmlDoc to JSON (JavaScript)'
  },
  // HTTP
  {
    text: `var reply = Http.get("http://somewhere/over/the/rainbow.txt", {});`,
    type: 'http',
    display: 'Send a HTTP GET request.',
    documentation:
      'Send a HTTP GET request. Returns a reply object containing;\n' +
      '\n' +
      'status - the http status-code\n' +
      'data - a string containing the data received\n' +
      'headers - an object containing the headers received\n'
  },
  {
    text: `var reply = Http.post("http://somewhere/over/the/rainbow.txt", "data=123", {});`,
    type: 'http',
    display: 'Send a HTTP POST request.',
    documentation:
      'Send a HTTP POST request. Returns a reply object containing;\n' +
      'status - the http status-code\n' +
      'data - a string containing the data received'
  },
  {
    text: `var reply = Http.put("http://somewhere/over/the/rainbow.txt", "data=123" {});`,
    type: 'http',
    display: 'Send a HTTP PUT request.',
    documentation:
      'Send a HTTP PUT request. Returns a reply object containing;\n' +
      'status - the http status-code\n' +
      'data - a string containing the data received'
  },
  {
    text: `var reply = Http.delete("http://somewhere/over/the/rainbow.txt", {});`,
    type: 'http',
    display: 'Send a HTTP DELETE request.',
    documentation:
      'Send a HTTP DELETE request. Returns a reply object containing;\n' +
      'status - the http status-code\n' +
      'data - a string containing the data received'
  },
  // SETTINGS
  {
    text: `Settings.CommandRetries`,
    type: 'settings',
    display: 'Settings.CommandRetries',
    documentation:
      'CommandRetries (int - read+write) defines the number of times a command is retried before it is considered to fail. Default is 3.'
  },
  {
    text: `Settings.CommandRetryDelays`,
    type: 'settings',
    display: 'Settings.CommandRetryDelays',
    documentation:
      ' (Array - read+write) defines the delays in milliseconds between each retry. Default is [100, 200, 400, 800, 1600]. When the number of retries exceed the given delays the last value in this array is used for all overflowing retries.'
  },
  // LOG
  {
    text: `Log.warn('key','text');`,
    type: 'log',
    display: 'Log.warn("key", "text")',
    documentation: 'Inserts a warning in the log.'
  },
  {
    text: `Log.info('key','text');`,
    type: 'log',
    display: "Log.info('key','text')",
    documentation: 'Inserts a informational line in the log.'
  },
  // WINDOW
  {
    text: `var title = Window.title();`,
    type: 'window',
    display: 'var title = Window.title()',
    documentation: 'Get the title of the main window.'
  },
  {
    text: `Window.minimize();`,
    type: 'window',
    display: 'Window.minimize()',
    documentation: 'Minimize the main window.'
  },
  {
    text: `Window.isMinimized()`,
    type: 'window',
    display: 'Window.isMinimized()',
    documentation: 'Check if the main window is minimized.'
  },
  {
    text: `Window.maximize();`,
    type: 'window',
    display: 'Window.maximize()',
    documentation: 'Maximize the main window.'
  },
  {
    text: `Window.isMaximized();`,
    type: 'window',
    display: 'Window.isMaximized()',
    documentation: 'Check if the main window is maximized.'
  },
  {
    text: `Window.focus();`,
    type: 'window',
    display: 'Window.focus()',
    documentation: 'Put focus on the main window.'
  },
  {
    text: `Window.restore();`,
    type: 'window',
    display: 'Window.restore()',
    documentation: 'Restore the main window to a previous state and location.'
  },
  {
    text: `Window.sendKeys("keys");`,
    type: 'window',
    display: 'Window.sendKeys("keys");',
    documentation:
      'Send keyboard events (simulated typing) to a window. Supports special strings for sending special keys.\n\n' +
      'keys - the keys to send - this is a string\n' +
      'options - optional object with options for sendkeys, supported options:\n' +
      'focus - [bool] whether to focus the window prior to sending the keys'
  },
  {
    text: `Window.modalShown();`,
    type: 'window',
    display: 'Window.modalShown()',
    documentation: 'Get whether or not a modal (dialog) is shown.'
  },
  {
    text: `Window.withTitleShown("My Window");`,
    type: 'window',
    display: 'Window.withTitleShown("My Window")',
    documentation: 'Get whether or not a window with the given title is shown.'
  },
  {
    text: `Window.dim(100);`,
    type: 'window',
    display: 'Window.dim(100)',
    documentation:
      'Dims the window owned by the flow.' +
      'PARAMETERS\n' +
      'level the amount of dimming, 0-255. 255 is opaque and 0 is transparent.'
  },
  // APP
  {
    text: `var loc = App.location();`,
    type: 'app',
    display: 'var loc = App.location()',
    documentation:
      'Returns the current location (if applicable for the given application type – non-webapps do not support this).'
  },
  {
    text: `App.navigate("url");`,
    type: 'app',
    display: 'App.navigate("url")',
    documentation:
      'Navigates to the given url. If the url is relative (e.g. somefolder/somefile.html) it will get appended to the current url.'
  },
  {
    text: `App.session().write('mykey', myvalue);`,
    type: 'app',
    display: "App.session().write('mykey', 'myvalue')",
    documentation:
      'Store a value in the current session storage. This will be available across flows and for all applications.'
  },
  {
    text: `var v = App.session().read('mykey');`,
    type: 'app',
    display: "var v = App.session().read('mykey')",
    documentation: 'Read a value stored in the current session.'
  },
  {
    text: `App.session().delete('mykey');`,
    type: 'app',
    display: "App.session().delete('mykey')",
    documentation: 'Delete a value.'
  },
  {
    text: `App.quit();`,
    type: 'app',
    display: 'App.quit()',
    documentation:
      'Quits the application - be aware that this is a hard shutdown and the user will not be prompted to save any information before the application exits.'
  },
  // Sticky
  {
    text: `Sticky.open("name", options);`,
    type: 'sticky',
    display: 'Sticky.open("name", options)',
    documentation:
      'Open a new sticky window with the given name and opts. The function can be called multiple times with the same name argument in order to update an already showing sticky.'
  },
  {
    text: `var m = Sticky.model('mySticky');`,
    type: 'sticky',
    display: "var m = Sticky.model('mySticky')",
    documentation: 'Get the model used to construct the sticky,'
  },
  {
    text: `Sticky.close('mySticky');`,
    type: 'sticky',
    display: "Sticky.close('mySticky')",
    documentation: 'Close a named sticky.'
  },
  {
    text: `Sticky.hide('mySticky');`,
    type: 'sticky',
    display: "Sticky.hide('mySticky')",
    documentation: 'Hide a named sticky.'
  },
  {
    text: `Sticky.show('mySticky');`,
    type: 'sticky',
    display: "Sticky.show('mySticky')",
    documentation: 'Show a previously hidden sticky.'
  },
  // TIMER
  {
    text: `Timer.start('myTimer');`,
    type: 'timer',
    display: "Timer.start('myTimer')",
    documentation:
      'Start a named timer. If you invoke this method twice with the same name (argument) you’ll reset the timer every time.'
  },
  {
    text: `Timer.log('myTimer', 'A message goes here');`,
    type: 'timer',
    display: "Timer.log('myTimer', 'A message goes here');",
    documentation:
      'Log an event on a named timer. Useful only in combination with our Analytics product. The logged event will contain the name of the timer, the milliseconds since the timer was started and the given message.'
  },
  {
    text: `Timer.stop('myTimer', true);`,
    type: 'timer',
    display: "Timer.stop('myTimer', true)",
    documentation: 'Stop a named timer.'
  },
  // NOTIFICATIONS
  {
    text: `Notification.show('hello', 'Seasonal greetings!', 'Felice navidad', {});`,
    type: 'notifications',
    display: "Notification.show('hello', 'Seasonal greetings!', 'Felice navidad', {})",
    documentation: 'Shows a notification.'
  },
  {
    text: `Notification.update('hello', 'Seasonal greetings anew!', 'Merry Christmas', {});`,
    type: 'notifications',
    display: "Notification.update('hello', 'Seasonal greetings anew!', 'Merry Christmas', {})",
    documentation: 'Update the information in an already shown notification.'
  },
  {
    text: `Notification.close('hello');`,
    type: 'notifications',
    display: "Notification.close('hello')",
    documentation:
      'Close an open notification. Notifications will automatically be hidden but this can force that action.'
  },
  // TASK
  {
    text: `Task.run(fun)`,
    type: 'task',
    display: 'Task.run(fun)',
    documentation:
      'Use the run method to start a new task.\n' +
      '\n' +
      'PARAMETERS\n' +
      'fun - a function to in parallel'
  },
  {
    text: `Task.waitAll(tasks, 1000)`,
    type: 'notifications',
    display: 'Task.waitAll(tasks, 1000)',
    documentation:
      'This is used to wait until all the tasks given as arguments complete or given milliseconds elapse.\n' +
      '\n' +
      'PARAMETERS\n' +
      'tasks - an [array of tasks or javascript functions] to run asynchronously (and then wait for)\n' +
      'timeout [int] denoting the max number of milliseconds to wait for the tasks to complete\n' +
      'RETURNS\n' +
      'A [bool] indicating wether or not all tasks completed.\n' +
      '\n'
  },
  {
    text: `Task.waitAny(tasks, 1000)`,
    type: 'notifications',
    display: 'Task.waitAny(tasks, 1000)',
    documentation:
      'This is used to wait until one of the tasks given as arguments completes or given milliseconds elapse.\n' +
      '\n' +
      'PARAMETERS\n' +
      'tasks - an [array of tasks or javascript functions] to run asynchronously (and then wait for one of)\n' +
      'timeout [int] denoting the max number of milliseconds to wait for any of the task to complete\n' +
      'RETURNS\n' +
      'An [int] denoting the index of the first task to complete or -1 if no tasks complete within given deadline.'
  },
  // GUID
  {
    text: `var guid = Guid.get();`,
    type: 'guid',
    display: 'var guid = Guid.get()',
    documentation: 'Returns a new random standard globally unique identifier'
  },
  // TABLE
  {
    text: `Table.map('foo', 'A');`,
    type: 'table',
    display: "Table.map('foo', 'A')",
    documentation:
      'The .map function will parse a table as a map, meaning that it will use a given column as an index. This is mainly useful if there is a column with unique values to use for the index. The returned structure will be a map with the column headers as keys.'
  },
  {
    text: `Table.rows('foo');`,
    type: 'table',
    display: "Table.rows('foo')",
    documentation: 'The .rows function will return the raw table as a javascript array of arrays.'
  },
  // ENV
  {
    text: `Env.userName;`,
    type: 'env',
    display: 'Env.userName',
    documentation: 'Get the username for the current user.'
  },
  {
    text: `Env.machineName;`,
    type: 'env',
    display: 'Env.machineName',
    documentation: 'Get the name of the machine.'
  },
  {
    text: `Env.userDomain;`,
    type: 'env',
    display: 'Env.userDomain',
    documentation: 'Get the domain for the current user.'
  },
  {
    text: `Env.primaryScreen;`,
    type: 'env',
    display: 'Env.primaryScreen',
    documentation: 'Get information about the primary screen of the local machine.'
  },
  {
    text: `Env.screens;`,
    type: 'env',
    display: 'Env.screens',
    documentation: 'Get information about all the screens attached to the local machine.'
  }
]

const fieldTypes = {
  FieldActionOptions: {
    'deadline?: number': 'How long to wait for the action before continuing',
    'reflectionDepth?: number': 'How many levels to include for e.g. inspect',
    'useFieldCache?: boolean': 'Use a cache to speed up the field detection',
    'fieldCacheExpiry?: number': 'How old a field-cache entry are you willing to use (in seconds)',
    'file?: boolean': 'File mode',
    'includeChildren?: boolean': 'Include children in inspect invocation?',
    'click?: string': 'Which type of click to perform',
    'opticalIndex?: number': '',
    'opticalOffset?: string': 'Which type of offset to use for optical fields'
  },
  IField: {
    'click(opts?: FieldActionOptions): void;': 'Click the field',
    'clickWithOffset(x:int, y:int, opts?: FieldActionOptions): void;':
      'Click the field with an (x,y) offset',
    'simulateClick(opts?: FieldActionOptions): void;':
      'Same as click but works better on fields which track the mouse-events directly',
    'simulateClickWithOffset(x:int, y:int, opts?: FieldActionOptions): void;':
      'Simulate click with an (x,y) offset',
    'rightClick(opts?: FieldActionOptions): void;': '',
    'rightClickWithOffset(x:int, y:int, opts?: FieldActionOptions): void;': '',
    'doubleClick(opts?: FieldActionOptions): void;': '',
    'doubleClickWithOffset(x:int, y:int, opts?: FieldActionOptions): void;': '',
    'clickCell(row: string | number, column: string | number, opts?: FieldActionOptions): void;':
      '',
    'read(): any;': '',
    'exists(): boolean;': '',
    'inspect(opts?: FieldActionOptions): any;': '',
    'input(value: string): void;': '',
    'inputNative(value: string): void;': '',
    'inputNativeWithDelay(value: string, delay: number): void;': '',
    'select(value: string , opts?: FieldActionOptions): void;': '',
    'selectIndex(index: number, opts?: FieldActionOptions): void;': '',
    'selectWithOffset(value: string, offset: number): void;': '',
    'selectWithOffsetAndSkip(value: string, offset: number, skip: number): void;': '',
    'editcell(row: string | number, column: string | number, value: string, opts?: FieldActionOptions): void;':
      '',
    'highlight():void': '',
    'highlightWithColor(color: string):void': '',
    'lowlight():void': ''
  }
}

/** Build the completions from examples stored in registry and dynamic content
 *  such as available flows etc
 */
const buildCompletions = (examples, word, app, flow, fields, allFlows) => {
  // The global suggestions are all examples loaded from default or registry
  const globalSuggestions = filterByWord(examples, word)

  // Include  snippets
  const jsSnippets = filterByWord(commonJavascriptSuggestions, word)

  // Generate Flow.run and Flow.include suggestions
  let flowSuggestions = generateFlowsSuggestions(word, allFlows)

  return _.concat(flowSuggestions, globalSuggestions, jsSnippets)
}

// The regex used to extract info from the id of the stored suggestions
const suggestionIdRegex = /(\w+)_(\d+\.\d+)/

const resolveSuggestions = version => {
  return new Promise((resolve, reject) => {
    if (suggestionCache[version]) {
      resolve(suggestionCache[version])
    } else {
      getDataSource()
        .then(ds => ds.getAll('suggestion'))
        .then(results => {
          _.each(results, result => {
            let m = suggestionIdRegex.exec(result.id)
            if (m) {
              if (!suggestionCache[m[2]]) suggestionCache[m[2]] = {}
              suggestionCache[m[2]][m[1]] = result
            }
          })
          resolve(suggestionCache[version])
        })
        .catch(reject)
    }
  })
}

const filterByWord = (suggestions, word) => {
  return _.filter(
    suggestions,
    s =>
      !word ||
      word === '' ||
      s.text.toLowerCase().indexOf(word.toLowerCase()) !== -1 ||
      word.toLowerCase().indexOf(s.text.toLowerCase()) !== -1 ||
      (s.trigger &&
        (s.trigger.toLowerCase().indexOf(word.toLowerCase()) !== -1 ||
          word.toLowerCase().indexOf(s.trigger.toLowerCase()) !== -1))
  )
}

const generateFlowsSuggestions = (word, allFlows) => {
  // Remove closing paranthesis and closing quote
  word = word.replace(/[)|\]]$/, '').replace(/['|"]$/, '')

  let flows = _.map(allFlows, f => {
    return {
      text: `Flow.run('${f.name}', {${_.join(
        _.map(f.inputs, (i, idx: number) => `'${i.name}':'$\{${idx + 1}:${i.name}Value\}'`),
        ', '
      )}});`,
      type: 'run a flow',
      display: `Run the "${f.name}" flow`
    }
  })

  let includeFlows = _.map(_.filter(allFlows, f => f.type === 'MODULE'), f => {
    return {
      text: `Flow.include('${f.name}');`,
      type: 'include a flow module',
      display: `Include the "${f.name}" flow`
    }
  })

  let flowSuggestions = flows.concat(includeFlows)
  return filterByWord(flowSuggestions, word)
}

const generateLib = (fields, flow, interfaces) => {
  const lib = `
  ${interfaces}

  declare var Application : IApplication;

  declare interface FieldList {
    ${_.map(fields, f => `"${f.name}": IField;`).join('\n')}
  }

  declare interface InputList {
    ${_.map(flow.inputs, i => `"${i.name}": string;`).join('\n')}
  }

  declare interface IField {
    new(n: String | Object, app?: Application): IField
  }

  declare interface IApplication {
    new(config: String | Object, options?: Object): IApplication;
  }

  declare var Inputs: InputList;
  declare var Fields: FieldList;
  declare var Value: string;
  declare var exports: any;

  `
  // Signal to consumers of this value whether it contains api type definitions
  return { source: lib, hasApiDefinitions: !!interfaces }
}

const versionsForType = (type: ?string): Array<string> => {
  if (!type) return Object.keys(suggestionCache)
  const kvs = Object.keys(suggestionCache).map(key => ({ key, value: suggestionCache[key] }))
  return _.map(_.filter(kvs, v => v.value[type]), v => v.key)
}

/** Fetch the available versions.
 */
const getVersions = (type: ?string) => {
  return new Promise((resolve, reject) => {
    const versions = versionsForType(type)
    if (versions && versions.length > 0) {
      resolve(versions)
    } else {
      getDataSource()
        .then(ds => ds.getAll('suggestion'))
        .then(results => {
          _.each(results, result => {
            let m = suggestionIdRegex.exec(result.id)
            if (m) {
              if (!suggestionCache[m[2]]) suggestionCache[m[2]] = {}
              suggestionCache[m[2]][m[1]] = result
            }
          })
          resolve(versionsForType(type))
        })
        .catch(reject)
    }
  })
}

/* Generate TS properties */
const generateProperties = properties => {
  return _.map(properties, p => `/** ${p.doc} */\n${p.def}`).join('\n')
}

/* Generate a TS interface decl incl properties */
const generateInterfaceDeclaration = (type, version, properties) => {
  const props = generateProperties(properties)
  return `declare interface I${type}${version} {\n${props}\n}\n`
}

/* Generate a TS var decl */
const generateVarDeclaration = (varname, type) => {
  return `declare var ${varname}: I${type};\n`
}

/** The TypeScript lib contains the full API for all internal modules and
 *  will be generated by Manatee.
 */
const getTypeScriptLib = (version, fields, flow) => {
  return new Promise((resolve, reject) => {
    resolveSuggestions(version)
      .then(result => {
        // The external modules be concatenated into the interface IModule
        // properties to enable `Module["Foo v1.2.3"]` auto-completion
        const hasApiSuggestions = result.api && result.api.lib
        const externalModulesAsProperties = !hasApiSuggestions
          ? []
          : _.uniqBy(
              _.flatMap(_.filter(result.api.lib, t => t.external && t.version), t => {
                const version = '_' + t.version.replace(/\./g, '_')
                return [
                  { doc: '', def: `"${t.type} v${t.version}": I${t.type}${version};` },
                  { doc: '', def: `"${t.type}": I${t.type};` }
                ]
              }),
              'def'
            )

        // Generate a list of interface declarations
        let declarations = !hasApiSuggestions
          ? ''
          : _.map(result.api.lib, t => {
              // Declare the type (as an interface)
              // Module is given special treatment
              const version = t.version ? '_' + t.version.replace(/\./g, '_') : ''
              let decl = generateInterfaceDeclaration(
                t.type,
                version,
                t.type === 'Module'
                  ? t.properties.concat(externalModulesAsProperties)
                  : t.properties
              )
              // Versioned types also get a non-versioned declaration
              if (t.version) decl += generateInterfaceDeclaration(t.type, '', t.properties)
              // The global variable declaration (should only be included for non-external modules
              const variable = t.external ? '' : generateVarDeclaration(t.type, t.type)
              return decl + variable + '\n'
            }).join('\n')

        // Two versions are known to not support the string extensions
        if (version && version !== '1.27' && version !== '1.28')
          declarations += `\n${stringExtensions}`

        resolve(generateLib(fields, flow, declarations))
      })
      .catch(err => {
        console.warn('Using default TypeScript defs, errored with ' + err)
        const defaultInterfaces = _.map(fieldTypes, (props, t) =>
          generateInterfaceDeclaration(t, '', _.mapKeys(def => ({ def: def, doc: props[def] })))
        ).join('\n')

        resolve(generateLib(fields, flow, defaultInterfaces))
      })
  })
}

/** Completions are snippets generated from Manatee documentation and elsewhere.
 */
const getCompletions = (word, version, app, flow, fields, allFlows) => {
  return new Promise((resolve, reject) => {
    if (word == null) resolve([])

    // Load all suggestions from registry or cache
    resolveSuggestions(version)
      .then(result => {
        resolve(buildCompletions(result.suggestion.suggestions, word, app, flow, fields, allFlows))
      })
      .catch(err => {
        console.warn('Using default snippets, errored with ' + err)
        resolve(buildCompletions(defaultExamples, word, app, flow, fields, allFlows))
      })
  })
}

const self = {
  resolveSuggestions,
  getVersions,
  getCompletions,
  getTypeScriptLib
}

/**
 * Completions service
 * @module CodeCompletionsService
 * @namespace CodeCompletionsService
 * @version 1.0.0
 */
export default self
